import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, Cpu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

export default function MyHeadsets() {
  const [headsets, setHeadsets] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [h, p] = await Promise.all([api.get("/headsets"), api.get("/patients")]);
      setHeadsets(h.data);
      setPatients(p.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const assignPatient = async (headsetId, patientId) => {
    try {
      await api.patch(`/headsets/${headsetId}/assign-patient`, { patient_id: patientId || null });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Inventory"
        title="My Headsets"
        description="Headsets assigned to you by an administrator. Reassign each to a patient on your roster."
      />

      <div className="panel rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-purple-50/50">
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Headset</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Model</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Patient</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500">Last sync</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.12em] text-slate-500 text-right">Open</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={5} className="text-center py-12 text-slate-400">Loading…</TableCell></TableRow>
            : headsets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-16">
                  <Cpu className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                  <div className="text-sm text-slate-500">No headsets assigned to you yet. Ask an admin to assign one.</div>
                </TableCell>
              </TableRow>
            ) : headsets.map((h) => (
              <TableRow key={h.id} data-testid={`my-headset-row-${h.id}`} className="hover:bg-slate-50/60">
                <TableCell className="text-sm font-medium">{h.name || <span className="font-mono">{h.serial}</span>}{h.test ? <Badge variant="outline" className="ml-2 text-[10px] uppercase bg-sky-50 text-sky-800 border-sky-200">test</Badge> : null}{h.name ? <div className="font-mono text-[11px] text-slate-400">{h.serial}</div> : null}</TableCell>
                <TableCell className="text-sm text-slate-600">{h.model}</TableCell>
                <TableCell>
                  <Select value={h.patient_id || "none"} onValueChange={(v) => assignPatient(h.id, v === "none" ? null : v)}>
                    <SelectTrigger data-testid={`headset-patient-select-${h.id}`} className="h-8 text-xs min-w-[180px]">
                      <SelectValue placeholder="No patient" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No patient</SelectItem>
                      {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-xs text-slate-500 tabular-nums">
                  {h.last_seen ? new Date(h.last_seen).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/headsets/${h.id}`)} data-testid={`headset-open-${h.id}`}>
                    Open <ArrowUpRight className="h-3 w-3 ml-1" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
